import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { Gift, X } from 'lucide-react';

const GiftCardPopup = () => {
  const [showModal, setShowModal] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    setMounted(true);
    // Check if user has dismissed the modal before
    const hasSeenPopup = sessionStorage.getItem('giftCardPopupSeen');
    if (!hasSeenPopup) {
      // Show modal after 5 seconds on homepage
      const timer = setTimeout(() => {
        if (window.location.pathname === '/') {
          setShowModal(true);
        }
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleDismiss = () => {
    setShowModal(false);
    sessionStorage.setItem('giftCardPopupSeen', 'true');
    setDismissed(true);
  };

  // Floating Button (always visible on all pages)
  const FloatingButton = () => (
    <motion.div
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ delay: 1, type: 'spring', stiffness: 200 }}
      className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-[9999]"
    >
      <Link
        to="/gift-cards"
        data-testid="gift-card-floating-btn"
        className="flex items-center gap-2 bg-black text-white px-3 py-2 sm:px-4 sm:py-3 rounded-full shadow-lg hover:bg-gray-800 transition-all hover:scale-105 group"
      >
        <Gift size={18} className="group-hover:rotate-12 transition-transform sm:w-5 sm:h-5" />
        <span className="font-mono text-[10px] sm:text-xs uppercase tracking-wider hidden sm:inline">Gift Cards</span>
      </Link>
    </motion.div>
  );

  // Modal Popup
  const ModalPopup = () => (
    <AnimatePresence>
      {showModal && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-[99998]"
            onClick={handleDismiss}
            data-testid="gift-card-modal-backdrop"
          />
          
          {/* Modal - Fixed for mobile */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            className="fixed inset-4 sm:inset-auto sm:top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 z-[99999] sm:w-[90%] sm:max-w-md flex items-center justify-center"
            data-testid="gift-card-modal"
          >
            <div className="bg-[#F8F8F5] p-6 sm:p-8 relative shadow-2xl w-full max-h-[90vh] overflow-y-auto">
              {/* Close Button */}
              <button
                onClick={handleDismiss}
                className="absolute top-3 right-3 sm:top-4 sm:right-4 p-2 hover:bg-black/5 rounded-full transition-colors"
                data-testid="gift-card-modal-close"
              >
                <X size={20} />
              </button>

              {/* Content */}
              <div className="text-center pt-2">
                <div className="w-14 h-14 sm:w-16 sm:h-16 bg-black rounded-full flex items-center justify-center mx-auto mb-4">
                  <Gift size={24} className="text-white sm:w-7 sm:h-7" />
                </div>
                
                <h3 className="font-heading text-xl sm:text-2xl md:text-3xl font-bold mb-3">
                  Give the Gift of Art
                </h3>
                
                <p className="font-body text-sm sm:text-base text-gray-600 mb-6 leading-relaxed">
                  Perfect for birthdays, celebrations, or just because. Let someone special choose their own meaningful tattoo.
                </p>

                <div className="space-y-3">
                  <Link
                    to="/gift-cards"
                    onClick={handleDismiss}
                    className="block w-full bg-black text-white py-3 font-bold text-sm uppercase tracking-wider hover:bg-gray-800 transition-colors"
                    data-testid="gift-card-modal-cta"
                  >
                    Shop Gift Cards
                  </Link>
                  
                  <button
                    onClick={handleDismiss}
                    className="block w-full text-gray-500 py-2 text-sm hover:text-black transition-colors"
                  >
                    Maybe Later
                  </button>
                </div>
              </div>

              {/* Decorative tape effect */}
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-16 sm:w-20 h-5 sm:h-6 bg-amber-200/80 rotate-1" 
                   style={{ clipPath: 'polygon(5% 0%, 95% 5%, 100% 100%, 0% 95%)' }} />
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  if (!mounted) return null;

  return (
    <>
      {createPortal(<FloatingButton />, document.body)}
      {createPortal(<ModalPopup />, document.body)}
    </>
  );
};

export default GiftCardPopup;
